import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { ConsignmentTrackingControl } from "../../../../oceanTracking/TrackingPanel";
import { NotesDrawerShell } from "../Notes/NotesDrawerShell";
import { FilesDrawerShell } from "../PurchaseOrderDetails/Files/FilesDrawerShell";
import { useHandleMilestoneViewMutation } from "../../../../../store/apiSlice/newInterfaceApiSlice";
import { useNotification } from "../../../../../hooks/useNotification";
import { MILESTONE_ACTIONS, orderTypeForUrl, selectState } from "../../utils/tableDataElements";
import { Status } from "../Status/Status";
import { BoardCardIcons } from "../Board/BoardCardIcons";
import { BoardRelationshipGraphDrawer } from "../Board/BoardRelationshipGraphDrawer";
import { PurchaseOrderBoardCard } from "../Board/PurchaseOrderBoardCard";
import { ShipmentBoardCardTitle } from "../Board/ShipmentBoardCardTitle";
import { ShipmentBoardFooterMeta } from "../Board/ShipmentBoardFooterMeta";
import {
  resolveBoardCardFooterLabel,
  resolveBoardCardHeroText,
  useBoardCardHero,
} from "../Board/boardCardDisplay";
import { filterOrderByMilestone } from "../../../../../store/utils";
import { BoardLaneModeIcon } from "../Board/BoardLaneModeIcon.jsx";
import { shouldHideEmptyLane } from "../../../../shipment/constants/shipmentCombinedBoard.js";

/**
 * MilestoneBoard Component
 *
 * Displays a milestone-style Kanban board view for shipments or other orders.
 * Groups orders under milestone columns (e.g., "Uncategorized", "Packed", "Dispatched"),
 * supports drag & drop to change milestones, and shows notes and files.
 *
 * Dynamically builds milestone columns based on the selected view configuration.
 * Integrates with Redux to fetch milestone data and dispatch milestone state updates.
 *
 * @component
 *
 * @param {Object} selectedCurrentView - The current view object (with `id`, `value`, `is_view_rule_exists`).
 * @param {string} orderType - Type of orders being displayed (e.g. "shipment", "purchase").
 * @param {Array} shipmentOrders - The full list of orders/consignments to display on the board.
 * @param {Function} getConsignmentByRule - Function to refetch consignments, typically after notes edit.
 * @param {Function} handleSelectOrder - Function triggered on clicking inside a milestone tile container
 *                                        (used for multi-select or other parent handlers).
 *
 */

export const MilestoneBoard = ({
  selectedCurrentView,
  orderType,
  statusPalette,
  shipmentOrders: consignments,
  getConsignmentByRule,
  handleSelectOrder,
  hideEmptyLanesMode = "none",
}) => {
  const boardCardHero = useBoardCardHero(selectedCurrentView, orderType);
  const {
    allViews: {
      boardView: { milestone },
    },
    milestoneList,
    filteredConsignmentByRule,
  } = useSelector(state => state[selectState[orderType]]);
  const dispatch = useDispatch();
  const notify = useNotification();
  const [updateMilestoneView, { isLoading: moving }] = useHandleMilestoneViewMutation();

  const [isOpenNotes, setIsOpenNotes] = useState(false);
  const [isOpenFiles, setIsOpenFiles] = useState(false);
  const [orderIdForNotes, setOrderIdForNotes] = useState(null);
  const [orderIdForFiles, setOrderIdForFiles] = useState(null);
  const [graphRecord, setGraphRecord] = useState(null);

  const openNotesModal = (id, orderName, is_note_available, open_task_count = 0) => {
    setIsOpenNotes(true);
    setOrderIdForNotes({ id, name: orderName, is_note_available, open_task_count });
  };
  const closeNotesModal = () => {
    setIsOpenNotes(false);
    setOrderIdForNotes(null);
  };
  const openFilesDrawer = (id, orderName, recordDisplayName = "") => {
    setOrderIdForFiles({ id, name: orderName, recordDisplayName });
    setIsOpenFiles(true);
  };
  const closeFilesDrawer = () => {
    setIsOpenFiles(false);
    setOrderIdForFiles(null);
  };
  const openGraphDrawer = (id, orderName) => {
    setGraphRecord({ id, name: orderName });
  };
  const closeGraphDrawer = () => {
    setGraphRecord(null);
  };

  const milestoneListForLocal = useMemo(() => {
    const order = [{ name: "Uncategorized", id: "Uncategorized" }];
    const allMilestoneId = {};
    milestoneList.forEach(item => (allMilestoneId[item.id] = item));
    (milestone.allViews[selectedCurrentView.value] || []).forEach(item => {
      if (allMilestoneId[item]) {
        order.push({
          name: allMilestoneId[item]["name"],
          id: item,
          color: allMilestoneId[item].meta?.color,
        });
      }
    });
    return order;
  }, [selectedCurrentView.value, milestone, milestoneList]);

  const dispatchMilestoneFilter = useCallback(
    data => {
      const action = MILESTONE_ACTIONS[orderType];
      if (action) {
        dispatch(action(data));
      }
    },
    [dispatch, orderType],
  );

  const currentData = useMemo(() => {
    if (orderType === "shipment" || orderType === "purchase") {
      return consignments;
    }
    if (selectedCurrentView.id && selectedCurrentView.is_view_rule_exists) {
      return filteredConsignmentByRule?.results || filteredConsignmentByRule;
    }
    return consignments;
  }, [orderType, selectedCurrentView, filteredConsignmentByRule, consignments]);

  const { filterd: filterShipmentByMilestone, mapConsignments: consignmentMappedById } = useMemo(
    () => filterOrderByMilestone(currentData, milestoneList),
    [currentData, milestoneList],
  );

  useEffect(() => {
    if (!["shipment", "purchase"].includes(orderType) && currentData) {
      dispatchMilestoneFilter(currentData);
    }
  }, [dispatchMilestoneFilter, currentData, milestoneList, orderType]);

  const handleDragStart = (e, sourceIndex, targetIndex) => {
    e.dataTransfer.setData("text/plain", `${sourceIndex}-${targetIndex}`);
  };

  const handleDragOver = e => {
    e.preventDefault();
  };

  /**
   * Requests a milestone move; consignments update the list cache in the mutation.
   * @param {React.DragEvent} e The drop event.
   * @param {number|string} targetIndex The destination milestone ID or Uncategorized.
   * @return {void}
   */
  const handleDrop = async (e, targetIndex) => {
    e.preventDefault();
    e.stopPropagation();
    const [sourceIndex, targetIndexFromDrag] = e.dataTransfer.getData("text/plain").split("-");
    if (moving || sourceIndex === String(targetIndex) || !Number(targetIndexFromDrag)) return;
    try {
      await updateMilestoneView({
        id: targetIndex,
        sourceIndex,
        data: { ids: [Number(targetIndexFromDrag)], module: orderTypeForUrl[orderType].module },
      }).unwrap();
    } catch (error) {
      const data = error?.data || {};
      notify({
        type: "error",
        message: data.detail || "Failed to update milestone.",
      });
    }
  };

  return (
    <>
      <NotesDrawerShell
        isOpen={isOpenNotes && Boolean(orderIdForNotes)}
        onClose={closeNotesModal}
        recordName={orderIdForNotes?.name}
        openTaskCount={orderIdForNotes?.open_task_count}
        key={orderIdForNotes?.id}
        orderId={orderIdForNotes?.id}
        isNoteAvailable={orderIdForNotes?.is_note_available}
        setOrderIdForNotes={setOrderIdForNotes}
        orderType={orderType}
        getConsignmentByRule={getConsignmentByRule}
        selectedCurrentView={selectedCurrentView}
      />
      <FilesDrawerShell
        isOpen={isOpenFiles && Boolean(orderIdForFiles)}
        onClose={closeFilesDrawer}
        orderId={orderIdForFiles?.id}
        orderName={orderIdForFiles?.name}
        recordDisplayName={orderIdForFiles?.recordDisplayName}
        orderType={orderType}
        getConsignmentByRule={getConsignmentByRule}
      />
      <BoardRelationshipGraphDrawer
        orderType={orderType}
        isOpen={Boolean(graphRecord)}
        onClose={closeGraphDrawer}
        record={graphRecord}
        onOpenFiles={openFilesDrawer}
        onOpenNotes={openNotesModal}
      />
      {milestoneListForLocal.length > 0 &&
        milestoneListForLocal.map((milestoneName, parentInd) => {
          const milestoneCount = filterShipmentByMilestone[milestoneName.id]?.length || 0;
          if (
            (orderType === "shipment" || orderType === "purchase") &&
            shouldHideEmptyLane(hideEmptyLanesMode, "milestone", milestoneCount)
          ) {
            return null;
          }
          return (
          <div key={milestoneName.name} className="board">
            <div className="boardHeader">
              <h3
                className={`colorBox ${milestoneName.color}${
                  orderType === "shipment" || orderType === "purchase"
                    ? " boardHeaderWithModeIcon"
                    : ""
                }`}
              >
                {(orderType === "shipment" || orderType === "purchase") && (
                  <BoardLaneModeIcon kind="milestone" />
                )}
                <p className="tickBox"></p> <p>{milestoneName.name}</p>{" "}
              </h3>
              <p className="countNo">{filterShipmentByMilestone[milestoneName.id]?.length || 0}</p>
            </div>
            <div
              className="tilesContainer"
              onClick={handleSelectOrder}
              key={milestoneName.name}
              onDragOver={handleDragOver}
              onDrop={e => handleDrop(e, milestoneName.id)}
            >
              {filterShipmentByMilestone[milestoneName.id]?.length > 0 &&
                filterShipmentByMilestone[milestoneName.id]?.map(orderId => {
                  const order = consignmentMappedById[orderId];
                  if (orderType === "purchase") {
                    return (
                      <PurchaseOrderBoardCard
                        key={order.id}
                        order={order}
                        orderType={orderType}
                        boardCardHero={boardCardHero}
                        draggable={!moving}
                        onDragStart={e => handleDragStart(e, milestoneName.id, order.id)}
                        onOpenFiles={openFilesDrawer}
                        onOpenNotes={openNotesModal}
                        onOpenGraph={openGraphDrawer}
                      />
                    );
                  }
                  return (
                    <div
                      key={order.id}
                      className={`boardTile ${orderType === "shipment" ? "shipmentBoardTile" : ""}`}
                      draggable={!moving}
                      onDragStart={e => handleDragStart(e, milestoneName.id, order.id)}
                    >
                      {orderType === "shipment" ? (
                        <>
                          <div className="tileHeader">
                            <div className="left">
                              <input
                                className="checkBox"
                                type="checkbox"
                                value={JSON.stringify(order)}
                              />
                              <ShipmentBoardCardTitle
                                orderId={order.id}
                                name={resolveBoardCardHeroText(order, orderType, boardCardHero)}
                              />
                            </div>
                            <BoardCardIcons
                              order={order}
                              onOpenFiles={openFilesDrawer}
                              onOpenNotes={openNotesModal}
                              onOpenGraph={openGraphDrawer}
                            />
                          </div>
                          <div className="facilityAndAsn">
                            <div className="destinationFacility">
                              <span>Destination facility</span>
                              <strong>
                                {order.to_warehouse?.name || order.to_warehouse || "—"}
                              </strong>
                            </div>
                            <div className="shipmentDetails">
                              <div>
                                <span>ASN</span>
                                <strong>{order.asn?.name || "Pending"}</strong>
                              </div>
                            </div>
                          </div>
                          <div className="tileFooter">
                            <ShipmentBoardFooterMeta
                              orderId={order.id}
                              reference={resolveBoardCardFooterLabel(order, orderType, boardCardHero)}
                              etaDisplay={
                                order.eta || order.estimated_date
                                  ? format(
                                      order.eta || order.estimated_date,
                                      "dd MMM yyyy, hh:mm a",
                                    )
                                  : "Pending"
                              }
                            />
                            <div className="trackerActions">
                              <ConsignmentTrackingControl
                                consignment={order.id}
                                shipmentNumber={order.name}
                                onTrackingUpdated={getConsignmentByRule}
                                deferQuery
                                initialHasTracking={order.has_ocean_tracking}
                                renderTrigger={({ disabled, hasTracking, label, onClick }) => (
                                  <button
                                    className="trackDetails"
                                    type="button"
                                    disabled={disabled}
                                    title={label}
                                    onClick={onClick}
                                  >
                                    {hasTracking ? "Show Tracker" : "Track"}{" "}
                                    <span aria-hidden="true">→</span>
                                  </button>
                                )}
                              />
                              <div className="statusAndPriority">
                                <Status statusName={order.status} palette={statusPalette} />
                              </div>
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="tileHeader">
                            <div className="left">
                              <input
                                className="checkBox"
                                type="checkbox"
                                value={JSON.stringify(order)}
                              />
                              <Link to={`${order.id}`}>{order.name} </Link>
                            </div>

                            <BoardCardIcons
                              order={order}
                              onOpenFiles={openFilesDrawer}
                              onOpenNotes={openNotesModal}
                            />
                          </div>

                          <div className="orderInfo">
                            <p>To Facility: {order.to_warehouse}</p>
                            <p>
                              Company: {order.tracking ? order.tracking?.logistics_partner : ""}
                            </p>
                            <p>ASN number: {order.asn ? order.asn.name : ""} </p>
                            <Status statusName={order.status} palette={statusPalette} />
                          </div>
                          <p className="eta">
                            ETA:{" "}
                            {order.eta || order.estimated_date
                              ? format(order.eta || order.estimated_date, "dd MMM yyyy, hh:mm a")
                              : "No eta time"}
                          </p>
                        </>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>
          );
        })}
    </>
  );
};
